"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ChevronDown, ChevronUp } from "lucide-react"
import Link from "next/link"

export type CookieBannerStrings = {
    title: string
    intro: string
    privacyLink: string
    privacyHref: string
    details: string
    essentialTitle: string
    essentialText: string
    analyticsTitle: string
    analyticsText: string
    reject: string
    save: string
    acceptAll: string
}

export const cookieBannerStringsDe: CookieBannerStrings = {
    title: "🍪 Datenschutzhinweis",
    intro: "Wir nutzen Cookies, um unsere Website funktionsfähig zu halten und Ihre Erfahrung zu verbessern. Einige Cookies sind notwendig, andere sind optional.",
    privacyLink: "Mehr in unserer Datenschutzerklärung →",
    privacyHref: "/datenschutz",
    details: "Cookie-Details",
    essentialTitle: "✅ Notwendige Cookies",
    essentialText: "Für die Funktion der Website erforderlich (Dark Mode, Session). Können nicht deaktiviert werden.",
    analyticsTitle: "📊 Analytics (Google Analytics 4)",
    analyticsText: "Hilft uns, die Website zu verbessern. Anonyme Daten zu Besuchen, Seiten und Konversionen.",
    reject: "Ablehnen",
    save: "Einstellungen speichern",
    acceptAll: "Alle akzeptieren",
}

export const cookieBannerStringsRu: CookieBannerStrings = {
    title: "🍪 Уведомление о cookies",
    intro: "Мы используем cookies, чтобы сайт работал корректно и становился удобнее. Часть cookies необходима, остальные — по вашему выбору.",
    privacyLink: "Подробнее в политике конфиденциальности (на немецком) →",
    privacyHref: "/datenschutz",
    details: "Подробнее о cookies",
    essentialTitle: "✅ Необходимые cookies",
    essentialText: "Нужны для работы сайта (тёмная тема, сессия). Отключить нельзя.",
    analyticsTitle: "📊 Аналитика (Google Analytics 4)",
    analyticsText: "Помогает нам улучшать сайт. Анонимные данные о посещениях, страницах и конверсиях.",
    reject: "Отклонить",
    save: "Сохранить настройки",
    acceptAll: "Принять все",
}

export function CookieBanner({ strings = cookieBannerStringsDe }: { strings?: CookieBannerStrings } = {}) {
    const [isVisible, setIsVisible] = useState(false)
    const [showDetails, setShowDetails] = useState(false)
    const [analyticsConsent, setAnalyticsConsent] = useState(false)

    useEffect(() => {
        // Check if user has already made a choice
        const consentGiven = localStorage.getItem("cookie_consent_essential")
        if (!consentGiven) {
            // Show banner after a short delay for smooth entrance
            const timer = setTimeout(() => setIsVisible(true), 1000)
            return () => clearTimeout(timer)
        }
    }, [])

    const saveConsent = (analytics: boolean) => {
        localStorage.setItem("cookie_consent_essential", "true")
        localStorage.setItem("cookie_consent_analytics", analytics ? "true" : "false")
        setIsVisible(false)
        window.dispatchEvent(new Event("cookie-consent-update"))
    }

    const handleAcceptAll = () => saveConsent(true)
    const handleRejectAll = () => saveConsent(false)
    const handleAcceptCustom = () => saveConsent(analyticsConsent)

    if (!isVisible) return null

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-center pointer-events-none">
            <Card className="max-w-2xl w-full shadow-lg border-primary/20 bg-background/95 backdrop-blur pointer-events-auto">
                <CardContent className="flex flex-col gap-4 p-4 md:p-6">
                    {/* Header */}
                    <div>
                        <p className="font-semibold mb-2">{strings.title}</p>
                        <p className="text-sm text-muted-foreground mb-2">
                            {strings.intro}
                        </p>
                        <Link href={strings.privacyHref} className="text-xs text-primary hover:underline">
                            {strings.privacyLink}
                        </Link>
                    </div>

                    {/* Cookie Details (Toggle) */}
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors py-2 min-h-[44px]"
                    >
                        <span>{strings.details}</span>
                        {showDetails ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </button>

                    {/* Expandable Details */}
                    {showDetails && (
                        <div className="space-y-3 pb-3 border-t pt-3 text-xs">
                            <div>
                                <p className="font-semibold mb-1">{strings.essentialTitle}</p>
                                <p className="text-muted-foreground">
                                    {strings.essentialText}
                                </p>
                            </div>
                            <div>
                                <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
                                    <input
                                        type="checkbox"
                                        checked={analyticsConsent}
                                        onChange={(e) => setAnalyticsConsent(e.target.checked)}
                                        className="w-5 h-5"
                                    />
                                    <span className="font-semibold">{strings.analyticsTitle}</span>
                                </label>
                                <p className="text-muted-foreground mt-1 ml-8">
                                    {strings.analyticsText}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <Button
                            variant="outline"
                            onClick={handleRejectAll}
                            className="flex-1 min-h-[44px]"
                        >
                            {strings.reject}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleAcceptCustom}
                            className="flex-1 min-h-[44px]"
                        >
                            {strings.save}
                        </Button>
                        <Button
                            onClick={handleAcceptAll}
                            className="flex-1 min-h-[44px]"
                        >
                            {strings.acceptAll}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}
